// @ts-ignore
import template from "./menu.html";

import FilterButtons from "./FilterButtons";
import ContentButtons from "./ContentButtons";
import ToolButtons from "../../enum/TOOL_PRESETS";

import renderButtons from "./renderButtons";
import adjustFontSize from "../../tools/adjustFontSize";
import renderTools from "./renderTools";
import reset from "./reset";

import { ILanguage, LANGUAGES } from "../../i18n/Languages";

import css from "./menu.css";
import { pluginConfig } from "@/globals/pluginConfig";
import { userSettings, saveUserSettings, setUserStateSettings, setUserProfile } from "@/globals/userSettings";
import { changeLanguage } from "@/i18n/changeLanguage";
import toggleMenu from "./toggleMenu";
import { $widget } from "../widget/widget";
import { PROFILES } from "../../enum/PROFILE_PRESETS";
import { t } from "../../i18n/translate";
import pageStructure from "@/tools/pageStructure";
import renderFilter from "./renderFilter";
import VISUAL_PRESETS from "@/enum/VISUAL_PRESETS";
import EXTRA_PRESETS from "@/enum/EXTRA_PRESETS";
import runAccessibility from "./runAccessibility";
import toggleScreenReader from "./toggleScreenReader";

function renderProfiles() {
    return PROFILES.map(profile => {
        const settings = userSettings;
        const isActive = settings?.states?.activeProfile === profile.key;
        return `
            <button class="asw-profile-btn ${isActive ? 'asw-selected' : ''}" 
                 data-key="${profile.key}" 
                 type="button"
                 aria-label="${t(profile.label)}"
                 aria-pressed="${isActive}">
                <div class="asw-profile-icon">
                    ${profile.icon}
                </div>
                <div class="asw-profile-content">
                    <div class="asw-profile-title asw-translate" data-translate="${profile.label}">${t(profile.label)}</div>
                    <div class="asw-profile-desc asw-translate" data-translate="${profile.description}">${t(profile.description)}</div>
                </div>
                <div class="asw-profile-toggle"></div>
            </button>
        `;
    }).join('');
}

export default function renderMenu() {
    const $container: HTMLElement = document.createElement("div");
    $container.innerHTML = `<style>${css}</style>` + template;

    const $menu = $container.querySelector(".asw-menu");
    if (pluginConfig?.position?.includes("right")) {
        $menu.style.right = '0px';
        $menu.style.left = 'auto';
    }

    $menu.querySelector(".content").innerHTML = renderButtons(ContentButtons);
    $menu.querySelector(".visual-navigation").innerHTML = renderButtons(VISUAL_PRESETS, 'asw-visual');
    $menu.querySelector(".tools").innerHTML = renderButtons(EXTRA_PRESETS, 'asw-tools');
    $menu.querySelector(".contrast").innerHTML = renderButtons(FilterButtons, 'asw-filter');
    $menu.querySelector(".profiles").innerHTML = renderProfiles();

    // *** States UI Rendering ***
    const states = userSettings?.states;

    const fontSize = Number(states?.fontSize) || 1;
    if (fontSize != 1) {
        $menu.querySelector(".asw-amount").innerHTML = `${fontSize * 100}%`;
    }

    if (states) {
        const buttons = Array.from($menu.querySelectorAll('.asw-btn'));

        Object.entries(states).forEach(([key, value]) => {
            if (value && key !== "fontSize") {
                const selector = key === "contrast" ? states[key] : key;
                const btn = buttons.find(b => b.dataset.key === selector);
                if (btn) btn.classList.add("asw-selected");
            }
        });
    }

    // *** Translations ***
    if (!userSettings.lang && pluginConfig?.lang) {
        userSettings.lang = pluginConfig.lang;
    }

    if (!LANGUAGES.some(lang => lang.code === userSettings.lang)) {
        userSettings.lang = "en";
    }

    const $lang = $menu.querySelector("#asw-language");
    const langOptions = LANGUAGES.map((lang: ILanguage) => `<option value="${lang.code}">${lang.label}</option>`).join('');
    $lang.innerHTML = langOptions;
    $lang.value = userSettings.lang;
    $lang.addEventListener("change", (event) => {
        changeLanguage(event.target.value);
    });

    if (userSettings.states.activeProfile) {
        $menu.querySelector(`.asw-profile-btn[data-key="${userSettings.states.activeProfile}"]`)?.classList?.add("asw-selected");
    }

    // *** Utils ***
    $container.querySelectorAll('.asw-menu-close, .asw-overlay').forEach((el) =>
        el.addEventListener('click', toggleMenu)
    );

    $container.querySelectorAll('.asw-menu-reset').forEach((el) =>
        el.addEventListener('click', reset)
    );

    // *** Controls ***
    $menu.querySelectorAll(".asw-plus, .asw-minus").forEach((el: HTMLElement) => {
        el.addEventListener("click", () => {
            const difference = 0.1;

            let fontSize = userSettings?.states?.fontSize || 1;
            if (el.classList.contains('asw-minus')) {
                fontSize -= difference;
            } else {
                fontSize += difference;
            }

            fontSize = Math.max(fontSize, 0.1);
            fontSize = Math.min(fontSize, 2);
            fontSize = Number(fontSize.toFixed(2));

            document.querySelector(".asw-amount").textContent = `${(fontSize * 100).toFixed(0)}%`;

            adjustFontSize(fontSize);
            userSettings.states.fontSize = fontSize;

            saveUserSettings();
        });
    });

    $menu.querySelectorAll(".asw-profile-btn").forEach((el: HTMLElement) => {
        el.addEventListener("click", () => {
            let key = el.dataset.key;
            let profile = PROFILES.find(p => p.key === key);
            let isSelected = !el.classList.contains("asw-selected");

            // Deselect all profiles
            $menu.querySelectorAll(".asw-profile-btn").forEach((pEl: HTMLElement) => {
                pEl.classList.remove("asw-selected");
            });

            if (isSelected) {
                el.classList.add("asw-selected");

                // Apply profile settings
                setUserProfile(key, profile.settings);
            } else {
                // Revert all settings the profile had applied
                const revertSettings: { [key: string]: any } = {};
                for (const settingKey in profile.settings) {
                    revertSettings[settingKey] = settingKey === 'contrast' ? false : false;
                }

                setUserProfile('', {})
            }

            // Sync UI for all buttons
            let updatedSettings = userSettings;
            $menu.querySelectorAll(".asw-btn").forEach((btn: HTMLElement) => {
                let btnKey = btn.dataset.key;
                let active = updatedSettings.states[btnKey];

                if (btn.classList.contains('asw-filter')) {
                    btn.classList.toggle('asw-selected', updatedSettings.states.contrast === btnKey);
                } else {
                    btn.classList.toggle('asw-selected', !!active);
                }
            });

            runAccessibility();

            if(profile.settings['screen-reader']) {
                toggleScreenReader(userSettings?.states?.['screen-reader']);
            }
        });
    });

    $menu.querySelectorAll(".asw-btn").forEach((el: HTMLElement) => {
        el.addEventListener("click", () => {
            const key = el.dataset.key;
            const isSelected = !el.classList.contains("asw-selected");

            // --- Contrast ---
            if (el.classList.contains("asw-filter")) {
                $menu.querySelectorAll(".asw-filter").forEach((el: HTMLElement) =>
                    el.classList.remove("asw-selected")
                );

                if (isSelected) {
                    el.classList.add("asw-selected");
                }

                userSettings.states.contrast = isSelected ? key : false;
                renderFilter();

                saveUserSettings();

                return;
            } else if (key === "page-structure") {
                el.classList.toggle('asw-selected', isSelected);

                pageStructure(true);
            }

            el.classList.toggle("asw-selected", isSelected);
            userSettings.states[key] = isSelected;
            renderTools();

            saveUserSettings();
        });
    });

    $menu.querySelector(".asw-btn[data-key='screen-reader']").addEventListener("click", () => {
        toggleScreenReader(userSettings.states['screen-reader']);
    });

    $widget.appendChild($container);

    return $container;
}